import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  makeStyles
} from "@material-ui/core";
import { useBannerImageCreateMutation } from "@saleor/banners/mutations";
import { useDynamicLinkListQuery } from "@saleor/banners/queries";
import { BannerDetails_banner } from "@saleor/banners/types/BannerDetails";
import { BannerImageCreateVariables } from "@saleor/banners/types/BannerImageCreate";
import { BannerUpdate_bannerUpdate_errors } from "@saleor/banners/types/BannerUpdate";
import { bannerListUrl, bannerUrl } from "@saleor/banners/urls";
import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { Tab, TabContainer } from "@saleor/components/Tab";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useStateFormProps from "@saleor/hooks/useStateFromProps";
import { maybe } from "@saleor/misc";
import { getChoices } from "@saleor/products/utils/data";
import { SearchMalls_search_edges_node } from "@saleor/searches/types/SearchMalls";
import { SearchSales_search_edges_node } from "@saleor/searches/types/SearchSales";
import { SearchVouchers_search_edges_node } from "@saleor/searches/types/SearchVouchers";
import { FetchMoreProps } from "@saleor/types";
import createMultiAutocompleteSelectHandler from "@saleor/utils/handlers/multiAutocompleteSelectChangeHandler";
import { dateTimePhTimeZone } from "@saleor/utils/smop/timeZonePH";
import { useCreateBannerValidation } from "@saleor/utils/validators/banners";
import { hasNoError } from "@saleor/utils/validators/sales";
import { RawDraftContentState } from "draft-js";
import React from "react";
import { useIntl } from "react-intl";

import {
  BannerVerticalEnum,
  SmopBannerTypeEnum,
  SmopStatusEnum
} from "../../../types/globalTypes";
import BannerBrands from "../BannerBrands";
import BannerCategories from "../BannerCategories";
import BannerCollections from "../BannerCollections";
import BannerCouponCollections from "../BannerCouponCollections";
import BannerCoupons from "../BannerCoupons/BannerCoupons";
import BannerDateTime from "../BannerDateTime";
import BannerDeepLink from "../BannerDeepLink";
import BannerDynamicLinksVertical from "../BannerDynamicLinksVertical";
import BannerImage from "../BannerImage";
import BannerInfo from "../BannerInfo";
import BannerMallList from "../BannerMallList";
import BannerProducts from "../BannerProducts";

export enum BannerDetailsPageTab {
  brands = "brands",
  categories = "categories",
  collections = "collections",
  couponCollections = "couponCollections",
  products = "products",
  verticals = "verticals",
  coupons = "coupons"
}

const useStyles = makeStyles(
  {
    columns: {
      "& div div": {
        width: "100%"
      },
      alignItems: "center",
      display: "flex",
      justifyContent: "space-between"
    },
    frmAction: {
      justifyContent: "center"
    },
    frmBtn: {
      "&.active": {
        "& span": {
          color: "#fff"
        },
        backgroundColor: "#285BAD"
      },
      "&:hover": {
        "& span": {
          color: "#fff"
        },
        backgroundColor: "#285BAD"
      },
      color: "#0030FF",
      padding: "6px 30px"
    },
    frmTxt: {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      fontSize: "18px",
      justifyContent: "center",
      minHeight: "100px"
    }
  },
  { name: "BannerUpdatePage" }
);

export interface BannerDetailsFormData {
  description: string;
  endDate: string;
  endTime: string;
  image: string;
  isSplashBanner: boolean;
  mall: string;
  mallName: string;
  name: string;
  saleId: string;
  saleName: string;
  startDate: string;
  startTime: string;
  status: SmopStatusEnum;
  subDescription: string;
  type: SmopBannerTypeEnum;
  voucherId: string;
  voucherName: string;
  dynamicLinkId: string;
  verticals: BannerVerticalEnum[];
}
export interface BannerDetailsPageProps {
  activeTab: BannerDetailsPageTab;
  data: BannerDetails_banner;
  disabled: boolean;
  errors: BannerUpdate_bannerUpdate_errors[];
  fetchMoreMalls: FetchMoreProps;
  malls?: SearchMalls_search_edges_node[];
  sales?: SearchSales_search_edges_node[];
  saveButtonBarState: ConfirmButtonTransitionState;
  vouchers?: SearchVouchers_search_edges_node[];
  lastTabLocation: any;
  onBack: () => void;
  onDelete: () => void;
  onMallAssign: () => void;
  onMallUnassign: (id: string) => void;
  fetchMalls: (query: string) => void;
  fetchSales: (query: string) => void;
  fetchVouchers: (query: string) => void;
  id: string;
  onSubmit: (data: BannerDetailsFormData) => void;
  onTabClick: (index: BannerDetailsPageTab) => void;
  onProductAssign: () => void;
  onProductUnassign: (id: string) => void;
  onBrandAssign: () => void;
  onBrandUnassign: (id: string) => void;
  onCollectionAssign: () => void;
  onCollectionUnassign: (id: string) => void;
  onCategoryAssign: () => void;
  onCategoryUnassign: (id: string) => void;
  onGenerateDeeplink: (id: string) => void;
  onCouponAssign: () => void;
  onCouponUnassign: () => void;
  onCouponCollectionAssign: () => void;
  onCouponCollectionUnassign: () => void;
}
const BannerDetailsPage: React.FC<BannerDetailsPageProps> = ({
  activeTab,
  data,
  disabled,
  errors,
  fetchMoreMalls,
  malls,
  sales,
  saveButtonBarState,
  vouchers,
  lastTabLocation,
  fetchMalls,
  fetchSales,
  fetchVouchers,
  id,
  onSubmit,
  onBack,
  onDelete,
  onMallAssign,
  onMallUnassign,
  onTabClick,
  onProductAssign,
  onProductUnassign,
  onBrandAssign,
  onBrandUnassign,
  onCollectionAssign,
  onCollectionUnassign,
  onCategoryAssign,
  onCategoryUnassign,
  onGenerateDeeplink,
  onCouponAssign,
  onCouponUnassign,
  onCouponCollectionAssign,
  onCouponCollectionUnassign
}) => {
  const intl = useIntl();
  const notify = useNotifier();
  const classes = useStyles("");
  const navigate = useNavigator();

  const [validator, setValidator] = React.useState({
    endDate: null,
    name: null,
    startDate: null,
    verticals: null
  });

  const [openModal, setOpenModal] = React.useState("");
  const [newImageLink, setNewImageLink] = React.useState(null);

  const featuredInChoices = [
    {
      label: "Homepage",
      value: BannerVerticalEnum.HOMEPAGE
    },
    {
      label: "Food",
      value: BannerVerticalEnum.FOOD
    },
    {
      label: "Grocery",
      value: BannerVerticalEnum.GROCERY
    },
    {
      label: "ShopSM",
      value: BannerVerticalEnum.SM_FULFILLED
    },
    {
      label: "Shops",
      value: BannerVerticalEnum.SHOPS
    },
    {
      label: "Cinema",
      value: BannerVerticalEnum.CINEMA
    },
    {
      label: "In Mall Deals",
      value: BannerVerticalEnum.IN_MALL_DEALS
    }
  ];

  const verticalProperCase = (vertical: BannerVerticalEnum) => {
    switch (vertical) {
      case BannerVerticalEnum.HOMEPAGE:
        return "Homepage";
      case BannerVerticalEnum.FOOD:
        return "Food";
      case BannerVerticalEnum.GROCERY:
        return "Grocery";
      case BannerVerticalEnum.SM_FULFILLED:
        return "ShopSM";
      case BannerVerticalEnum.SHOPS:
        return "Shops";
      case BannerVerticalEnum.CINEMA:
        return "Cinema";
      case BannerVerticalEnum.IN_MALL_DEALS:
        return "In Mall Deals";
      default:
        return " ";
    }
  };

  const selectedVerticals = maybe(() =>
    data.verticals.map(vertical => ({
      id: vertical,
      name: verticalProperCase(vertical)
    }))
  );

  const [selectedFeatured, setSelectedFeatured] = useStateFormProps(
    getChoices(selectedVerticals || [])
  );

  React.useEffect(() => {
    if (
      id &&
      data &&
      !activeTab &&
      !data.product &&
      !data.collection &&
      !data.brand &&
      !data.category &&
      !data.page &&
      !data.coupon &&
      !data.couponCollection
    ) {
      navigate(
        bannerUrl(id, {
          activeTab: BannerDetailsPageTab.products
        }),
        false,
        false,
        false
      );
    } else if (data && !activeTab && data.product) {
      navigate(
        bannerUrl(id, {
          activeTab: BannerDetailsPageTab.products
        }),
        false,
        false,
        false
      );
    } else if (data && !activeTab && data.collection) {
      navigate(
        bannerUrl(id, {
          activeTab: BannerDetailsPageTab.collections
        }),
        false,
        false,
        false
      );
    } else if (data && !activeTab && data.brand) {
      navigate(
        bannerUrl(id, {
          activeTab: BannerDetailsPageTab.brands
        }),
        false,
        false,
        false
      );
    } else if (data && !activeTab && data.coupon) {
      navigate(
        bannerUrl(id, {
          activeTab: BannerDetailsPageTab.coupons
        }),
        false,
        false,
        false
      );
    } else if (data && !activeTab && data.couponCollection) {
      navigate(
        bannerUrl(id, {
          activeTab: BannerDetailsPageTab.couponCollections
        }),
        false,
        false,
        false
      );
    } else if (data && !activeTab && data.category) {
      navigate(
        bannerUrl(id, {
          activeTab: BannerDetailsPageTab.categories
        }),
        false,
        false,
        false
      );
    } else if (data && !activeTab && data.page) {
      navigate(
        bannerUrl(id, {
          activeTab: BannerDetailsPageTab.verticals
        }),
        false,
        false,
        false
      );
    }
  }, [data, activeTab, id]);

  const BrandTab = Tab(BannerDetailsPageTab.brands);
  const CollectionTab = Tab(BannerDetailsPageTab.collections);
  const ProductsTab = Tab(BannerDetailsPageTab.products);
  const CategoryTab = Tab(BannerDetailsPageTab.categories);
  const VerticalTab = Tab(BannerDetailsPageTab.verticals);
  const CouponTab = Tab(BannerDetailsPageTab.coupons);
  const CouponCollectionsTab = Tab(BannerDetailsPageTab.couponCollections);

  const initialForm: BannerDetailsFormData = {
    description:
      data && data.descriptionJson && JSON.parse(data.descriptionJson),
    dynamicLinkId: (data && data.page && data.page.id) || "",
    endDate:
      data && data.endDate
        ? dateTimePhTimeZone(maybe(() => data.endDate, "")).date
        : null,
    endTime:
      data && data.endDate
        ? dateTimePhTimeZone(maybe(() => data.endDate, "")).time
        : null,
    image: data && data.imageUrl ? data.imageUrl.url : null,
    isSplashBanner: data && data.isSplashBanner,
    mall: (data && data.mall && data.mall.id) || null,
    mallName: (data && data.mall && data.mall.name) || "All Malls",
    name: (data && data.name) || "",
    saleId: (data && data.sale && data.sale.id) || "",
    saleName: (data && data.sale && data.sale.name) || "",
    startDate:
      data && data.startDate
        ? dateTimePhTimeZone(maybe(() => data.startDate, "")).date
        : null,
    startTime:
      data && data.startDate
        ? dateTimePhTimeZone(maybe(() => data.startDate, "")).time
        : null,
    status: SmopStatusEnum.ACT,
    subDescription:
      (data && data.voucher && data.voucher.description) ||
      (data && data.sale && data.sale.description) ||
      "",
    type: data && data.type,
    verticals: data?.verticals || [],
    voucherId: (data && data.voucher && data.voucher.id) || "",
    voucherName: (data && data.voucher && data.voucher.code) || ""
  };

  const initialDescription = maybe<RawDraftContentState>(() =>
    JSON.parse(data.descriptionJson)
  );

  const [
    createBannerImage,
    createBannerImageOpts
  ] = useBannerImageCreateMutation({
    onCompleted: data => {
      const imageError = data.bannerImageCreate.errors.find(
        error => error.field === ("image" as keyof BannerImageCreateVariables)
      );
      if (imageError) {
        notify({
          status: "error",
          text: data.bannerImageCreate.errors[0].message
        });
      } else {
        setNewImageLink(data.bannerImageCreate.banner.imageUrl.url);
      }
    }
  });

  const handleSubmit = event => {
    const validate = useCreateBannerValidation(event);
    setValidator(validate);

    if (hasNoError(validate)) {
      setOpenModal("save");
    }
  };

  const callback = (formData, click, action) => {
    if (click) {
      if (action === "save") {
        onSubmit(formData);
      } else {
        onDelete();
      }
    }
    setOpenModal("");
  };

  const { data: dynamicLinkList } = useDynamicLinkListQuery({
    displayLoader: true,
    variables: {
      first: 100
    }
  });

  const showProductTab =
    data &&
    !data.brand &&
    !data.collection &&
    !data.category &&
    !data.page &&
    !data.coupon &&
    !data.couponCollection;
  const showCollectionTab =
    data &&
    !data.product &&
    !data.brand &&
    !data.category &&
    !data.page &&
    !data.coupon &&
    !data.couponCollection;
  const showBrandTab =
    data &&
    !data.product &&
    !data.collection &&
    !data.category &&
    !data.page &&
    !data.coupon &&
    !data.couponCollection;
  const showCategoryTab =
    data &&
    !data.product &&
    !data.collection &&
    !data.brand &&
    !data.page &&
    !data.coupon &&
    !data.couponCollection;
  const showVerticalTab =
    data &&
    !data.brand &&
    !data.collection &&
    !data.category &&
    !data.product &&
    !data.coupon &&
    !data.couponCollection;
  const showCouponTab =
    data &&
    !data.brand &&
    !data.collection &&
    !data.category &&
    !data.product &&
    !data.page &&
    !data.couponCollection;
  const showCouponCollectionTab =
    data &&
    !data.brand &&
    !data.collection &&
    !data.category &&
    !data.product &&
    !data.page &&
    !data.coupon;

  const breadcrumbDirectory = [
    {
      name: "Banners",
      url: "/discounts/banners"
    },
    {
      name: data && data.name
    }
  ];

  return (
    <Form initial={initialForm} onSubmit={handleSubmit}>
      {({ data: formData, change, hasChanged, submit, toggleValue }) => {
        const handleSelectFeatured = createMultiAutocompleteSelectHandler(
          toggleValue,
          setSelectedFeatured,
          selectedFeatured,
          featuredInChoices
        );
        const handleSubmit = () => {
          if (selectedFeatured.length === 0) {
            notify({
              status: "error",
              text: "Please select a page to Featured in"
            });
          } else {
            submit();
          }
        };
        return (
          <Container>
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                marginTop: "10px"
              }}
            >
              <PageBackNavigation
                onBack={() => navigate(bannerListUrl(lastTabLocation))}
              />
              <PageHeader title={data && data.name} />
            </div>
            <div>
              <BannerInfo
                data={formData}
                disabled={disabled}
                errors={errors}
                fetchMalls={fetchMalls}
                fetchMoreMalls={fetchMoreMalls}
                fetchSales={fetchSales}
                fetchVouchers={fetchVouchers}
                handleNameChange={change}
                initialDescription={initialDescription}
                isEdit={true}
                localError={validator}
                malls={malls}
                onChange={change}
                sales={sales}
                vouchers={vouchers}
                featuredInDisplayValue={selectedFeatured}
                featuredChoices={featuredInChoices}
                setSelectedFeatured={setSelectedFeatured}
                onFeaturedChange={handleSelectFeatured}
              />
              <CardSpacer />
              <div className={classes.columns}>
                <BannerDateTime
                  updatePage={true}
                  clientErrors={validator}
                  disabled={
                    disabled || data.type !== SmopBannerTypeEnum.ANNOUNCEMENT
                  }
                  errors={errors}
                  onChange={change}
                  data={formData}
                />
                <BannerDeepLink
                  linkId={data && data.id}
                  linkUrl={data && data.deeplinkUrl}
                  shortLinkUrl={data && data.shortDynamicLink}
                  onGenerateDeeplink={onGenerateDeeplink}
                />
              </div>
              <CardSpacer />
              <BannerImage
                data={formData}
                loading={createBannerImageOpts.loading}
                onChange={change}
                onImageUpload={file =>
                  createBannerImage({
                    variables: {
                      banner: data.id,
                      image: file
                    }
                  })
                }
                uploadedLink={newImageLink}
              />
              <CardSpacer />
              {formData.type === SmopBannerTypeEnum.ANNOUNCEMENT && (
                <BannerMallList
                  disabled={disabled}
                  malls={maybe(() => data.malls)}
                  emptyText="Assigned to all malls"
                  title="Malls"
                  onMallAssign={onMallAssign}
                  onMallUnassign={onMallUnassign}
                />
              )}
              <CardSpacer />
            </div>
            <CardSpacer />
            {formData.type === SmopBannerTypeEnum.ANNOUNCEMENT && (
              <TabContainer>
                {showProductTab && (
                  <ProductsTab
                    isActive={activeTab === BannerDetailsPageTab.products}
                    changeTab={onTabClick}
                  >
                    {intl.formatMessage({
                      defaultMessage: "Product",
                      description: "product header"
                    })}
                  </ProductsTab>
                )}
                {showCollectionTab && (
                  <CollectionTab
                    isActive={activeTab === BannerDetailsPageTab.collections}
                    changeTab={onTabClick}
                  >
                    {intl.formatMessage({
                      defaultMessage: "Product Collection",
                      description: "product collection header"
                    })}
                  </CollectionTab>
                )}
                {showBrandTab && (
                  <BrandTab
                    isActive={activeTab === BannerDetailsPageTab.brands}
                    changeTab={onTabClick}
                  >
                    {intl.formatMessage({
                      defaultMessage: "Brand",
                      description: "brand header"
                    })}
                  </BrandTab>
                )}
                {showCategoryTab && (
                  <CategoryTab
                    isActive={activeTab === BannerDetailsPageTab.categories}
                    changeTab={onTabClick}
                  >
                    {intl.formatMessage({
                      defaultMessage: "Category",
                      description: "category header"
                    })}
                  </CategoryTab>
                )}
                {showVerticalTab && (
                  <VerticalTab
                    isActive={activeTab === BannerDetailsPageTab.verticals}
                    changeTab={onTabClick}
                  >
                    {intl.formatMessage({
                      defaultMessage: "Vertical",
                      description: "vertical header"
                    })}
                  </VerticalTab>
                )}
                {showCouponTab && (
                  <CouponTab
                    isActive={activeTab === BannerDetailsPageTab.coupons}
                    changeTab={onTabClick}
                  >
                    {intl.formatMessage({
                      defaultMessage: "Coupon",
                      description: "coupon header"
                    })}
                  </CouponTab>
                )}
                {showCouponCollectionTab && (
                  <CouponCollectionsTab
                    isActive={
                      activeTab === BannerDetailsPageTab.couponCollections
                    }
                    changeTab={onTabClick}
                  >
                    {intl.formatMessage({
                      defaultMessage: "Coupon Collection",
                      description: "coupon collection tab header"
                    })}
                  </CouponCollectionsTab>
                )}
              </TabContainer>
            )}
            {formData.type === SmopBannerTypeEnum.ANNOUNCEMENT && (
              <>
                <CardSpacer />
                {activeTab === BannerDetailsPageTab.products && (
                  <BannerProducts
                    product={maybe(() => data.product)}
                    onProductAssign={onProductAssign}
                    onProductUnassign={onProductUnassign}
                    onRowClick={() => undefined}
                  />
                )}
                {activeTab === BannerDetailsPageTab.collections && (
                  <BannerCollections
                    collection={maybe(() => data.collection)}
                    onCollectionAssign={onCollectionAssign}
                    onCollectionUnassign={onCollectionUnassign}
                    onRowClick={() => undefined}
                  />
                )}
                {activeTab === BannerDetailsPageTab.brands && (
                  <BannerBrands
                    brand={maybe(() => data.brand)}
                    onBrandAssign={onBrandAssign}
                    onBrandUnassign={onBrandUnassign}
                    onRowClick={() => undefined}
                  />
                )}
                {activeTab === BannerDetailsPageTab.categories && (
                  <BannerCategories
                    category={maybe(() => data.category)}
                    onCategoryAssign={onCategoryAssign}
                    onCategoryUnassign={onCategoryUnassign}
                    onRowClick={() => undefined}
                  />
                )}
                {activeTab === BannerDetailsPageTab.verticals && (
                  <BannerDynamicLinksVertical
                    submitdata={formData}
                    onChange={change}
                    dynamicLinkList={maybe(
                      () =>
                        dynamicLinkList?.dynamicLinks?.edges.map(
                          edge => edge.node
                        ),
                      []
                    )}
                  />
                )}
                {activeTab === BannerDetailsPageTab.coupons && (
                  <BannerCoupons
                    coupon={maybe(() => data.coupon)}
                    onCouponAssign={onCouponAssign}
                    onCouponUnassign={onCouponUnassign}
                  />
                )}
                {activeTab === BannerDetailsPageTab.couponCollections && (
                  <BannerCouponCollections
                    couponCollection={maybe(() => data.couponCollection)}
                    onCouponCollectionAssign={onCouponCollectionAssign}
                    onCouponCollectionUnassign={onCouponCollectionUnassign}
                  />
                )}
              </>
            )}
            <SaveButtonBar
              disabled={disabled || !hasChanged}
              onCancel={onBack}
              onDelete={onDelete}
              onSave={handleSubmit}
              state={saveButtonBarState}
            />
            <Dialog
              open={openModal !== ""}
              onClose={() => setOpenModal("")}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogContent className={classes.frmTxt}>
                {`Are you sure you want to ${openModal} this banner?`}
              </DialogContent>
              <DialogActions className={classes.frmAction}>
                <Button
                  className={classes.frmBtn}
                  id="btnNo"
                  color="primary"
                  type="button"
                  onClick={() => callback(formData, false, openModal)}
                >
                  No
                </Button>
                <Button
                  className={classes.frmBtn + " active"}
                  id="btnYes"
                  color="primary"
                  type="button"
                  onClick={() => callback(formData, true, openModal)}
                >
                  {openModal === "save" ? "Yes" : "Delete"}
                </Button>
              </DialogActions>
            </Dialog>
          </Container>
        );
      }}
    </Form>
  );
};
BannerDetailsPage.displayName = "BannerDetailsPage";
export default BannerDetailsPage;
